import { takeLatest, call, put, select } from "redux-saga/effects";
import * as ApiLocation from "store/location/location-api";
import { DrawerStatusEnum } from "typings/global.types";
import { removeUnnecessaryDocument, verifyDrivers } from "utils/functions";
import YmsNotification from "utils/functions/notification";
import { translate } from "utils/intl/IntlProvider";
import { getError, hasError } from "utils/requests/api.errors";
import { Creators as AttendanceCreators } from "../attendance/attendance-duck";
import * as Api from "./lobby-api";
import { Types, Creators } from "./lobby-duck";
import { IStepEntryPermissionStatus, IVehicleProfileByPlate, TrailerType } from "./lobby-model";

export const getFilterFromState = (state) => state.Lobby;
export const getFilterFromStateUserSession = (state) => state.UserSession;

export const onExtractRegisterData = (registersData) =>
  (Array.isArray(registersData?.items) ? registersData.items : []).map((item) => {
    return {
      id: item.yardRegisterId,
      carrier: item.carrier,
      vehiclePlate: item.vehiclePlate,
      process: item.process,
      inboundEventTime: item.inboundEventTime,
      action: true,
      trailersplate: item.trailersPlates?.map((itemTrailersPlates) => itemTrailersPlates),
      documents: removeUnnecessaryDocument(item.documents ?? [], "Chave NF"),
      gateEventId: item.id,
      checkinEventTime: item?.checkinEventTime,
      lenghtOfStay: item.lenghtOfStay,
    };
  });

export const onExtractRegisterDataCheckins = (registersData) =>
  (Array.isArray(registersData?.items) ? registersData.items : []).map((item) => {
    return {
      id: item.yardRegisterId,
      carrier: item.carrier,
      vehiclePlate: item.vehiclePlate,
      process: item.process,
      radiusInEventTime: item.radiusInEventTime,
      action: true,
      trailersplate: item.trailersPlates?.map((itemTrailersPlates) => itemTrailersPlates),
      documents: removeUnnecessaryDocument(item.documents ?? [], "Chave NF"),
      gateEventId: item.id,
      saleChannelDescription: item.saleChannelDescription,
      lenghtOfStay: item.lenghtOfStay,
      isReleased: item.isReleased,
    };
  });

const selectableTrailerTypes = [
  TrailerType.BOX_TRAILER,
  TrailerType.CURTAINSIDER,
  TrailerType.FLATBED,
  TrailerType.LIGHTWEIGHT_CURTAINSIDER,
  TrailerType.BULK_TRAILER,
  TrailerType.TANKER,
  TrailerType.OTHER,
];

export const normalizeTrailerType = (registerData) => {
  const rawTrailerType = registerData?.TrailerType ?? registerData?.trailerType;
  if (rawTrailerType == null) return undefined;
  const trailerType = typeof rawTrailerType === "number" ? rawTrailerType : Number(rawTrailerType);
  if (Number.isNaN(trailerType)) return undefined;

  return selectableTrailerTypes.includes(trailerType) ? trailerType : undefined;
};

export const extractVehicleProfileByPlate = (vehicleProfile): IVehicleProfileByPlate | null => {
  if (!vehicleProfile) {
    return null;
  }

  const trailerType = normalizeTrailerType(vehicleProfile);

  return {
    hasCheckin: vehicleProfile.hasCheckin === true,
    ...(trailerType != null ? { trailerType } : {}),
    ...(vehicleProfile.saleChannelId != null ? { saleChannelId: vehicleProfile.saleChannelId } : {}),
  };
};

export const onExtractItemRegisterData = (registerData) => {
  return {
    ...registerData,
    saleChannelDescription: registerData.saleChannel?.id,
    canEditVehiclePlate: registerData?.canEditVehiclePlate === true,
    trailerType: normalizeTrailerType(registerData),
  };
};

export function* onFetchItemAsync(action: any) {
  try {
    yield put(Creators.lobbyDrawerShowLoading());
    const lobbyState = yield select(getFilterFromState);
    const resp = yield call(Api.getYarnRegister, {
      yardRegisterId: action.yardRegisterId,
    });
    const respData = resp.data;
    const bodyRequest = verifyDrivers(respData, lobbyState.documentType);
    const register = onExtractItemRegisterData(bodyRequest);
    yield put(Creators.lobbyUpdateRegisterActive(register));
  } catch (err) {
    yield put(Creators.lobbyHideDrawerViewRegister());
    yield put(Creators.lobbyHideDrawerOutbound());
    console.error(err);
    const registrationErrorMessage = yield getError(err) ?? translate("register_data_informations_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.lobbyDrawerHideLoading());
  }
}

function addFrotaLegalData(bodyRequest, lobbyState) {
  return {
    ...bodyRequest,
    qualification: {
      verified: lobbyState.entryPermissionActive.verified,
      driver: {
        name: lobbyState.entryPermissionActive.driver.name,
        status: lobbyState.entryPermissionActive.driver.qualification,
        reason: lobbyState.entryPermissionActive.driver.reason,
      },
      vehicle: {
        plate: lobbyState.entryPermissionActive.vehicle.plate,
        description: lobbyState.entryPermissionActive.vehicle.description,
        status: lobbyState.entryPermissionActive.vehicle.qualification,
        reason: lobbyState.entryPermissionActive.vehicle.reason,
      },
    },
  };
}

function formatData(bodyRequest) {
  return {
    ...bodyRequest,
    qualification: null,
  };
}

export function* onSaveInboundAsync({ bodyRequest }: any) {
  try {
    const lobbyState = yield select(getFilterFromState);
    yield put(Creators.lobbyShowLoading());
    yield put(Creators.lobbyDisabledRegisterButton());
    const body = lobbyState.hasEntryPermission ? addFrotaLegalData(bodyRequest, lobbyState) : formatData(bodyRequest);
    yield call(Api.saveInbound, body);
    yield put(Creators.lobbyEnabledRegisterButton());
    yield put(Creators.lobbyHideDrawerInbound());
    yield put(Creators.lobbyHideDrawerCheckinRegister());
    const totalItemsCheckinPage = lobbyState.itemsCheckins;
    if (totalItemsCheckinPage.length === 1 && lobbyState.totalItemsCheckins > 10) {
      const pageIndexValue = lobbyState.filterCheckin.pageIndex - 1;
      yield put(Creators.lobbyChangeFieldValueCheckin("pageIndex", pageIndexValue));
    }
    const registrationSuccessfullyMessage = yield translate("registration_successfully!");
    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });
    yield put(Creators.lobbyClearDocuments());
    yield onFetchAllActivesAsync();
    yield onFetchAllCheckinsAsync();
  } catch (error) {
    console.error(error);
    yield put(Creators.lobbyEnabledRegisterButton());
    const registrationErrorMessage = yield getError(error) ?? translate("cannot_register");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.lobbyHideLoading());
  }
}

export function* onSaveOutboundAsync(action: any) {
  try {
    const lobbyState = yield select(getFilterFromState);
    yield put(Creators.lobbyShowLoading());
    yield call(Api.saveOutbound, {
      ...action.body,
    });
    yield put(Creators.lobbyHideDrawerOutbound());
    const totalItemsPage = lobbyState.items;
    if (totalItemsPage.length === 1 && lobbyState.totalItems > 10) {
      const pageIndexValue = lobbyState.filterActive.pageIndex - 1;
      yield put(Creators.lobbyChangeFieldValue("pageIndex", pageIndexValue));
    }
    const registrationSuccessfullyMessage = yield translate("registration_successfully!");
    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });
    yield onFetchAllActivesAsync();
    yield onFetchAllCheckinsAsync();
  } catch (error) {
    console.error(error);
    let registrationErrorMessage;
    if (hasError(error)) {
      registrationErrorMessage = yield getError(error);
    } else {
      registrationErrorMessage = yield translate("cannot_register");
    }
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.lobbyHideLoading());
  }
}

export function* onUpdateInboundAsync({ bodyRequest, form }: any) {
  try {
    yield put(Creators.lobbyShowLoading());
    const state = yield select(getFilterFromState);
    yield call(Api.updateInbound, bodyRequest, state.registerActive.id);
    yield put(Creators.lobbyHideDrawerViewRegister());
    const registrationSuccessfullyMessage = yield translate("update_successfully");
    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });
    yield put(Creators.lobbyUpdateDrawerViewMode(DrawerStatusEnum.View));
    yield put(Creators.lobbyClearDocuments());
    yield put(Creators.lobbyUpdateFormModified(false));
    form.resetFields();
    yield onFetchAllActivesAsync();
    yield onFetchAllCheckinsAsync();
  } catch (error) {
    const registrationErrorMessage = yield getError(error) ?? translate("cannot_register");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.lobbyHideLoading());
  }
}

export function* onSaveVehicleActionAsync(action: any) {
  try {
    yield put(Creators.lobbyShowLoading());
    yield put(Creators.lobbyDisabledButtonSaveVehicle());
    if (action.body.dock) {
      yield call(Api.saveRequestVehicleForDock, { ...action.body });
    } else {
      yield call(Api.saveRequestVehicleForGate, action.body.id);
    }
    yield put(Creators.lobbyHideModalVehicleAction());
    yield put(Creators.lobbyEnabledButtonSaveVehicle());
    const registrationSuccessfullyMessage = yield translate("vehicle_action_success_notification");
    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });
  } catch (error) {
    console.error(error);
    yield put(Creators.lobbyEnabledButtonSaveVehicle());
    const registrationErrorMessage = yield getError(error) ?? translate("vehicle_action_error_notification");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.lobbyHideLoading());
  }
}

export function* onFetchSaleChannelsAsync() {
  try {
    const saleChannelResponse = yield call(Api.getSaleChannels);
    const salesChannelList = saleChannelResponse.data;
    const saleChannel = { salesChannelList };
    yield put(Creators.lobbySaleChannelsList(saleChannel));
  } catch (error) {
    const requestErrorMessage = yield translate("could_not_load_sales_channel");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  }
}

export function* onFetchInboundProcessesAsync() {
  const userSessionState = yield select(getFilterFromStateUserSession);
  try {
    const eventTypeInbound = 1;
    const processesResponse = yield call(Api.getProcesses, {
      locationId: userSessionState.userData.locationId,
      eventType: eventTypeInbound,
    });

    const inboundProcessesList = processesResponse.data;
    const processes = { inboundProcessesList };
    yield put(Creators.lobbyInboundProcessesList(processes));
  } catch (error) {
    const requestErrorMessage = yield translate("could_not_load_processes");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  }
}

export function* onFetchOutboundProcessesAsync() {
  const userSessionState = yield select(getFilterFromStateUserSession);
  try {
    const eventTypeOutbound = 2;
    const processesResponse = yield call(Api.getProcesses, {
      locationId: userSessionState.userData.locationId,
      eventType: eventTypeOutbound,
    });
    const outboundProcessesList = processesResponse.data;
    const processes = { outboundProcessesList };
    yield put(Creators.lobbyOutboundProcessesList(processes));
  } catch (error) {
    const requestErrorMessage = yield translate("could_not_load_processes");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  }
}

export function* onFetchDocumentsAsync(action: any) {
  try {
    const documentsResponse = yield call(Api.getDocuments, {
      processCorrelationId: action.processCorrelationId,
      eventType: action.eventType,
    });
    const documentList = documentsResponse.data;
    const document = { documentsList: documentList };
    yield put(Creators.lobbyDocumentsList(document));
  } catch (error) {
    console.error(error);
    const requestErrorMessage = yield translate("could_not_load_documents");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  }
}

export function* onFetchAllActivesAsync() {
  try {
    yield put(Creators.lobbyInboundActiveShowLoading());
    const userSessionState = yield select(getFilterFromStateUserSession);
    const lobbyState = yield select(getFilterFromState);

    const resp = yield call(Api.getInboundsActives, {
      locationId: userSessionState.userData.locationId,
      _size: lobbyState.filterActive.itemsByPage,
      _page: lobbyState.filterActive.pageIndex,
      filter: lobbyState.searchFilter,
    });
    const registersData = resp.data;
    const registerList = onExtractRegisterData(registersData);
    yield put(Creators.lobbyRegistersActive(registerList, registersData.totalItems));
  } catch (err) {
    console.error(err);
  } finally {
    yield put(Creators.lobbyInboundActiveHideLoading());
  }
}

export function* onFetchAllCheckinsAsync() {
  try {
    yield put(Creators.lobbyInboundCheckinShowLoading());
    const userSessionState = yield select(getFilterFromStateUserSession);
    const lobbyState = yield select(getFilterFromState);
    if (lobbyState.hasCheckin === true) {
      const resp = yield call(Api.getInboundsCheckins, {
        locationId: userSessionState.userData.locationId,
        _size: lobbyState.filterCheckin.itemsByPage,
        _page: lobbyState.filterCheckin.pageIndex,
        filter: lobbyState.searchFilter,
      });
      const registersData = resp.data;
      const registerList = onExtractRegisterDataCheckins(registersData);
      yield put(Creators.lobbyRegistersCheckins(registerList, registersData.totalItems));
    }
  } catch (err) {
    console.error(err);
  } finally {
    yield put(Creators.lobbyInboundCheckinHideLoading());
  }
}

export function* onFetchHasConfigurationAsync() {
  try {
    yield put(Creators.lobbyShowLoading());
    const userSessionState = yield select(getFilterFromStateUserSession);
    const locationId = userSessionState?.userData?.locationId;

    if (!locationId || locationId <= 0) {
      const locationIdNotAvailableMessage = yield translate("location_id_not_available_to_fetch_unit_configuration");
      console.warn(locationIdNotAvailableMessage);
      return;
    }

    const resp = yield call(ApiLocation.getLocationHasConfiguration, { id: locationId });
    const hasConfiguration = resp.data;
    yield put(
      Creators.lobbyUpdateHasConfiguration(
        hasConfiguration.hasCheckin,
        hasConfiguration.allowRequestDockEntryAction,
        hasConfiguration.allowRequestVehicleInboundAction,
        hasConfiguration.hasFinishOperationAction,
        hasConfiguration.allowRequestVehicleEntryAuthorizationAction,
        hasConfiguration.hasAttendance,
        hasConfiguration.hasSaleChannel,
        hasConfiguration.hasCheckinSchedule,
        hasConfiguration.documentType,
        hasConfiguration.hasEntryPermission,
        hasConfiguration.hasRfidReading,
        hasConfiguration.plateFormatType
      )
    );
    yield put(Creators.lobbyHasConfigurationLoaded());
    if (resp.data.hasCheckin) {
      yield onFetchAllCheckinsAsync();
    }
  } catch (err) {
    console.error(err);
  } finally {
    yield put(Creators.lobbyHideLoading());
  }
}

export function* onFetchItemCheckinAsync(action: any) {
  try {
    yield put(Creators.lobbyDrawerShowLoading());
    const lobbyState = yield select(getFilterFromState);
    const resp = yield call(Api.getCheckinYardRegister, {
      yardRegisterId: action.yardRegisterId,
    });
    const itemCheckin = verifyDrivers(resp.data, lobbyState.documentType);
    const checkin = onExtractItemRegisterData(itemCheckin);
    yield put(Creators.lobbyCheckinUpdateRegisterActive(checkin));
  } catch (err) {
    yield put(Creators.lobbyHideDrawerCheckinRegister());
    yield put(AttendanceCreators.attendanceHideDrawerViewCheckinRegister());
    console.error(err);
    const registrationErrorMessage = yield getError(err) ?? translate("register_data_informations_could_not_be_loaded");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.lobbyDrawerHideLoading());
  }
}

export function* fetchVehicleProfileByPlate(vehiclePlate: string, notifyExistingCheckin = false) {
  try {
    yield put(Creators.lobbyUpdateVehicleProfileByPlate(null));
    const resp = yield call(Api.getVehicleProfileByPlate, { plate: vehiclePlate });
    const vehicleProfile = extractVehicleProfileByPlate(resp?.data);

    if (!vehicleProfile) {
      yield put(Creators.lobbyUpdateVehicleProfileByPlate({}));
      return;
    }

    if (vehicleProfile.hasCheckin && notifyExistingCheckin) {
      yield put(Creators.lobbyUpdateVehicleProfileByPlate({}));
      const existingCheckinMessage = yield translate("exist_check_in_to_this_plate");
      yield YmsNotification({
        type: "error",
        description: existingCheckinMessage,
      });
      return;
    }

    yield put(Creators.lobbyUpdateVehicleProfileByPlate(vehicleProfile));
  } catch (err) {
    console.error(err);
    yield put(Creators.lobbyUpdateVehicleProfileByPlate({}));
  }
}

export function* onFetchVehicleProfileByPlateAsync(action: any) {
  try {
    yield put(Creators.lobbyShowLoading());
    yield* fetchVehicleProfileByPlate(action.vehiclePlate, action.notifyExistingCheckin);
  } finally {
    yield put(Creators.lobbyHideLoading());
  }
}

export function* onFetchItemCheckinByVehiclePlateAsync(action: any) {
  try {
    yield put(Creators.lobbyShowLoading());
    yield put(Creators.lobbyUpdateVehicleProfileByPlate(null));
    const lobbyState = yield select(getFilterFromState);
    const resp = yield call(Api.getCheckinYardRegisterByVehiclePlate, {
      vehiclePlate: action.vehiclePlate,
    });
    const respData = resp?.data;
    if (respData === "" || respData === null || respData === undefined) {
      yield put(Creators.lobbyCheckinUpdateRegisterByVehiclePlateActive(null));
      yield* fetchVehicleProfileByPlate(action.vehiclePlate, false);
    } else {
      const itemCheckin = verifyDrivers(respData, lobbyState.documentType);
      const checkin = onExtractItemRegisterData(itemCheckin);
      yield put(Creators.lobbyCheckinUpdateRegisterByVehiclePlateActive(checkin));
    }
  } catch (err) {
    console.error(err);
    const requestErrorMessage = yield translate("could_not_load_existing_checkin");
    yield YmsNotification({
      type: "error",
      description: requestErrorMessage,
    });
  } finally {
    yield put(Creators.lobbyHideLoading());
  }
}

export function* onFetchItemVehicleEntryAuthorizationAsync(action: any) {
  try {
    yield put(Creators.lobbyDrawerShowLoading());
    const resp = yield call(Api.getEventsHistory, action.body.id);
    yield put(Creators.lobbyUpdateVehicleEntryAuthorizationRegisterActive(resp.data));
  } catch (err) {
    console.error(err);
    yield put(Creators.lobbyHideDrawerVehicleEntryAuthorization());
    const registrationErrorMessage = yield getError(err) ?? translate("could_not_load_events_history");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.lobbyDrawerHideLoading());
  }
}

export function* onFinishVehicleRegisterAsync(action: any) {
  try {
    const lobbyState = yield select(getFilterFromState);
    yield put(Creators.lobbyShowLoading());
    yield call(Api.finishVehicleRegister, action.body.yardRegisterId);
    yield put(Creators.lobbyHideDrawerViewRegister());
    const totalItemsPage = lobbyState.items;
    if (totalItemsPage.length === 1 && lobbyState.totalItems > 10) {
      const pageIndexValue = lobbyState.filterActive.pageIndex - 1;
      yield put(Creators.lobbyChangeFieldValue("pageIndex", pageIndexValue));
    }
    const registrationSuccessfullyMessage = yield translate("vehicle_finalize_successfully");

    yield YmsNotification({
      type: "success",
      description: registrationSuccessfullyMessage,
    });
    yield onFetchAllActivesAsync();
  } catch (error) {
    console.error(error);
    const registrationErrorMessage = yield getError(error) ?? translate("could_not_finish_vehicle");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.lobbyHideLoading());
  }
}

export function* onFetchEntryPermissionAsync(action: any) {
  try {
    yield put(Creators.lobbyEntryPermissionShowLoading());
    yield put(Creators.lobbyDisabledRegisterButton());
    yield put(Creators.lobbyUpdateStepEntryPermissionStatus(IStepEntryPermissionStatus.LoadingCheckDriverAndVehicle));
    const formatIdenticationDocument = action.identificationDocument.replace(/[.-]/g, "");
    const resp = yield call(Api.getEntryPermission, {
      vehicleId: action.vehiclePlate,
      driverId: formatIdenticationDocument,
      saleChannelType: action.saleChannelId,
    });

    if (resp.data.driver && resp.data.vehicle && !resp.data.driver.isFound && !resp.data.vehicle.isFound) {
      return yield put(Creators.lobbyUpdateStepEntryPermissionStatus(IStepEntryPermissionStatus.NotFoundDriverAndVehicle));
    }
    yield put(Creators.lobbyUpdateEntryPermissionActive(resp.data));
    yield put(Creators.lobbyUpdateStepEntryPermissionStatus(IStepEntryPermissionStatus.VerifiedDriverAndVehicle));
  } catch (error) {
    yield put(Creators.lobbyUpdateStepEntryPermissionStatus(IStepEntryPermissionStatus.RetryCheckDriverAndVehicle));

    console.error(error);
    const registrationErrorMessage = yield getError(error) ?? translate("unable_to_validate_data_in_frota_legal");
    yield YmsNotification({
      type: "error",
      description: registrationErrorMessage,
    });
  } finally {
    yield put(Creators.lobbyUpdateEntryPermissionButtonClicked());
    yield put(Creators.lobbyEnabledRegisterButton());
    yield put(Creators.lobbyEntryPermissionHideLoading());
  }
}

export default function* LobbySagas() {
  yield takeLatest(Types.LOBBY_FETCH_ITEM_ASYNC, onFetchItemAsync);
  yield takeLatest(Types.LOBBY_SAVE_INBOUND_ASYNC, onSaveInboundAsync);
  yield takeLatest(Types.LOBBY_SAVE_OUTBOUND_ASYNC, onSaveOutboundAsync);
  yield takeLatest(Types.LOBBY_UPDATE_INBOUND_ASYNC, onUpdateInboundAsync);
  yield takeLatest(Types.LOBBY_DOCUMENTS_FETCH_ASYNC, onFetchDocumentsAsync);
  yield takeLatest(Types.LOBBY_FETCH_ALL_ACTIVES_ASYNC, onFetchAllActivesAsync);
  yield takeLatest(Types.LOBBY_FETCH_ALL_CHECKINS_ASYNC, onFetchAllCheckinsAsync);
  yield takeLatest(Types.LOBBY_FETCH_HAS_CONFIGURATION_ASYNC, onFetchHasConfigurationAsync);
  yield takeLatest(Types.LOBBY_FETCH_ITEM_CHECKIN_ASYNC, onFetchItemCheckinAsync);
  yield takeLatest(Types.LOBBY_SALE_CHANNELS_FETCH_ASYNC, onFetchSaleChannelsAsync);
  yield takeLatest(Types.LOBBY_INBOUND_PROCESSES_FETCH_ASYNC, onFetchInboundProcessesAsync);
  yield takeLatest(Types.LOBBY_OUTBOUND_PROCESSES_FETCH_ASYNC, onFetchOutboundProcessesAsync);
  yield takeLatest(Types.LOBBY_SAVE_VEHICLE_ACTION_ASYNC, onSaveVehicleActionAsync);
  yield takeLatest(Types.LOBBY_FETCH_ITEM_CHECKIN_BY_VEHICLE_PLATE_ASYNC, onFetchItemCheckinByVehiclePlateAsync);
  yield takeLatest(Types.LOBBY_FETCH_VEHICLE_PROFILE_BY_PLATE_ASYNC, onFetchVehicleProfileByPlateAsync);
  yield takeLatest(Types.LOBBY_FETCH_ITEM_VEHICLE_ENTRY_AUTHORIZATION_ASYNC, onFetchItemVehicleEntryAuthorizationAsync);
  yield takeLatest(Types.LOBBY_FINISH_VEHICLE_REGISTER_ASYNC, onFinishVehicleRegisterAsync);
  yield takeLatest(Types.LOBBY_FETCH_ENTRY_PERMISSION_ASYNC, onFetchEntryPermissionAsync);
}
